<template>
  <hx-content-card class="CEModuleRenderer">
    <dynamic-content
      v-if="renderedContent"
      :id="CEModuleRendererId"
      class="rendered-content pagecontent"
      :content="renderedContent"
      @edit-popup="onEditPopUp"
    ></dynamic-content>
    <related-page-section
      :module-id="cemoduleId"
      :current-page-path="pagePath"
      @show-related-pages="$emit('showRelatedPages', $event)"
    ></related-page-section>
  </hx-content-card>
  <ContextMenuDialog ref="context-menu-dialog"></ContextMenuDialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { CSBase, CSFormDialog } from '@complispace/cs-design-system';
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import DynamicContent from '@/components/content/DynamicContent';
import CEContentEditorTipTap from '@/components/content/CEContentEditorTipTap';
import ContextMenuDialog from '@/components/content/ContextMenuDialog';
import RelatedPageSection from '@/components/content/RelatedPageSection';
import {
  mergeFieldToLowerCase,
  getEditableVariableContextKeyRenderValue,
  decodeHtmlEntities
} from '@/lib/util';
import { initializeLazyImages } from '@/lib/imageRenderUtils';
import { regularNumberedListOneLine } from '@/lib/mdRegular';
import {
  initializeLazyAssuranceDetails,
  addAssuranceTable,
  addNoDataString
} from '@/lib/assuranceDetailsUtils';
import { addAnchorLinkForHTML, getCreatorId, convertFormatTagsToHeadings } from '@/lib/htmlParser';
import TempPage from '../views/TempPage';

import nunjucks from 'nunjucks';
const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

// NOTE: Turning off autoescaping can be dangerous
nunjucks.configure('./', { autoescape: false });

export default {
  name: 'CEModuleRenderer',

  components: {
    TempPage,
    DynamicContent,
    CEContentEditorTipTap,
    CSFormDialog,
    ContextMenuDialog,
    RelatedPageSection
  },
  extends: CSBase,

  props: {
    cemoduleId: { type: Number, required: true },
    pagePath: { type: String, required: true },
    mdContent: { type: String, required: true },
    htmlContent: { type: String, required: false, default: '' },
    treeContent: { type: Object, required: false },
    applyContext: { type: Boolean, required: true },
    viewOnly: { type: Boolean, required: false, default: false },
    useHistoricalContext: { type: Boolean, required: false, default: false },
    isEndPage: { type: Boolean, required: true }
  },

  emits: [
    'showModuleToolbar',
    'getAnchorIds',
    'refresh',
    'next',
    'previous',
    'showRelatedPages',
    'rawPageError'
  ],

  computed: {
    ...mapState({
      navigation: (state) => state.navigation.navigation,
      pageNameMap: (state) => state.navigation.pageNameMap,
      pageOriginalNameMap: (state) => state.navigation.pageOriginalNameMap,
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain,
      role: (state) => state.authorisation.role,
      isPublicViewMode: (state) => state.appearance.isPublicViewMode,
      pageCommitOID: (state) => state.data.pageCommitOID
    }),

    ...mapGetters({
      contextItemValues: 'clientContext/values',
      previousContextItemValues: 'clientContext/previousValues',
      contextItems: 'clientContext/items',
      previousContextItems: 'clientContext/previousItems',
      isAdminDraftView: 'authorisation/isAdminDraftView',
      viewOnlyContextKeyValues: 'clientContext/viewOnlyContextKeyValues',
      hasAccess: 'authorisation/hasAccess'
    }),

    // Page.vue and HistoricalPrintRenderer co-mount a renderer each for the same module
    // and page; the link-fix pass below finds its own element by this id.
    CEModuleRendererId() {
      const suffix = this.useHistoricalContext ? '-historical' : '';
      return `CEModuleRenderer-${this.cemoduleId}-${this.snakeMe(this.pagePath)}${suffix}`;
    },

    isAdmin() {
      const roleId = parseInt(this.role, 10);
      if (roleId === 4 || roleId === 3 || roleId === 2) {
        return true;
      }
      return false;
    },

    renderedContent() {
      const contentString = this.htmlContent || this.mdContent;
      // In Public View mode, we do not load TOC and context keys and hence do not check for the pageNameMap and context keys and render the page.
      if (
        contentString &&
        this.cemoduleId &&
        (this.isPublicViewMode ||
          (Object.keys(this.pageNameMap).length > 0 &&
            (this.contextItemValues || this.previousContextItemValues)))
      ) {
        this.$emit('showModuleToolbar', true);

        // Transform inter, intra, intra-app and external links to
        // appropriate product links.
        const indexOfFirstNewLine = contentString.indexOf('\n');
        let content =
          indexOfFirstNewLine > 0 ? contentString.substring(indexOfFirstNewLine + 1) : '';
        content = this.parseLinks(content);
        if (content.trim().length === 0) {
          if (this.isEndPage) {
            content = 'No content is available for this module';
          }
        }

        try {
          const lines = content.split('\n');
          const constructedLines = [];
          let ifCount = 0;
          let endCount = 0;

          lines.forEach((line) => {
            const lineIfVars = line.match(/{%\s*.[^{}]+\s*%}/g);
            // eslint-disable-next-line no-param-reassign
            line = regularNumberedListOneLine(line);

            if (lineIfVars && lineIfVars.length > 0) {
              const ifDirectives = lineIfVars[0].match(/indirectives(\s*)[(（].*[)）]/gi);

              if (ifDirectives && ifDirectives.length > 0) {
                const cleanIfDirectives = ifDirectives[0]
                  .replace(/[\u2018\u2019]/g, '"')
                  .replace(/[\u201C\u201D]/g, '"');

                const directives = cleanIfDirectives.match(/"(.*?)"/g);
                constructedLines.push(
                  `<p style="color:grey; font-size:14; font-style:italic;"> [--- ${directives[0].substring(
                    1,
                    directives[0].length - 1
                  )} ---]</p>`
                );

                ifCount = 0;
                endCount = 0;
                return;
              }

              lineIfVars.forEach((item) => {
                if (item.indexOf('{% if ') >= 0 || item.indexOf('{%if ') >= 0) {
                  ifCount += 1;
                }

                if (
                  item.indexOf('{%endif%}') >= 0 ||
                  item.indexOf('{% endif%}') >= 0 ||
                  item.indexOf('{%endif %}') >= 0 ||
                  item.indexOf('{% endif %}') >= 0
                ) {
                  endCount += 1;
                }
              });

              if (endCount > ifCount) {
                const newline = line.replace(/{%\s*.[^{}]+\s*%}/g, '');
                constructedLines.push(newline);
                return;
              }
            }
            constructedLines.push(line);
          });

          let contextValues = null;

          const useHistorical = !!this.pageCommitOID || this.useHistoricalContext;

          if (this.viewOnly) {
            contextValues = this.viewOnlyContextKeyValues;
          } else {
            contextValues = useHistorical ? this.previousContextItemValues : this.contextItemValues;
          }
          // Convert Markdown to HTML
          const markdown = constructedLines.join('\n').trim();
          const contextKeyItems = useHistorical ? this.previousContextItems : this.contextItems;
          const formattedMarkdown = getEditableVariableContextKeyRenderValue(
            markdown,
            contextKeyItems,
            this.isAdmin,
            this.isAdminDraftView,
            this.pageCommitOID,
            this.client
          );

          let html = md.render(mergeFieldToLowerCase(formattedMarkdown));

          if (
            this.treeContent &&
            Object.prototype.hasOwnProperty.call(this.treeContent, 'assuranceData')
          ) {
            if (this.treeContent.assuranceData.isConfigured) {
              if (this.treeContent.assuranceData.document) {
                html += addAssuranceTable(this.treeContent.assuranceData.document);
              } else if (this.treeContent.assuranceData.document === null) {
                html += addNoDataString();
              } else {
                html += '<div id="assuranceDocumentDetails"></div>';
              }
            }
          }
          const creatorId = getCreatorId(this.navigation, this.cemoduleId, this.pagePath);
          const formattedHtml = convertFormatTagsToHeadings(html, creatorId);

          let htmlWithContextValues = nunjucks.renderString(formattedHtml, contextValues).trim();

          // need to decode validRenderedInstructions due too validRenderedInstructions formatting now
          // is &lt;span class=&quot;validContext&quot;&gt;test&lt;/span&gt;
          // need to convert to be as <span class=\"validContext\">test</span>
          htmlWithContextValues = decodeHtmlEntities(htmlWithContextValues);

          const { anchorIds, htmlWithHeadingId } = addAnchorLinkForHTML(htmlWithContextValues);

          this.$emit('getAnchorIds', anchorIds);
          this.$emit('rawPageError', false);
          return htmlWithHeadingId;
        } catch (error) {
          this.$emit('rawPageError', true);
          console.error('Error while rendering page content with context key values: ', error);
          // Try rendering
          return `<h3>There is a problem on this page.</h3>
                    <p>The content will have errors. Please report to your administrator immediately.</p>
                    <div style="border-style: solid; border-color:  rgb(var(--v-theme-warning));">
                    <h3>----Raw page data for reference only----</h3>
                    ${content
                      .replace(/{{/g, '<span v-pre>{{')
                      .replace(/}}/g, '}}</span>')
                      .replace(/{#/g, '<span v-pre>{#')
                      .replace(/#}/g, '#}</span>')
                      .trim()}
                    </div>`;
        }
      }

      this.$emit('showModuleToolbar', false);
      return null;
    }
  },

  watch: {
    renderedContent(value) {
      // FIXME: This does a second pass to fix links
      // in Commonmark HTML tables using DOM manipulation.
      // More robust for finding all links but doesn't support Vue
      // router-link so inter/intra-module links fully reload pages.
      // Gross. We should move away from commonmark, html tables?
      if (value) {
        this.contentLoad = true;
        setTimeout(() => {
          // Check for inter-module link
          const interModuleLinkRegex = /^https?:\/\/ce-viewer.com/;

          // Check for external link
          // If external link then leave as is - but add target="_blank"
          // so it opens in a new tab/window.

          /* eslint-disable no-useless-escape */
          const urlRegex = new RegExp(
            [
              '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?',
              '[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?',
              '(\/.*)?$'
            ].join('')
          );
          /* eslint-disable no-useless-escape */

          // Check for intra-module link - links within the same module
          // NOTE: This test is executed last as this regex can also be
          // triggered by the inter module link regex.
          const intraModuleLinkRegex = /^#/;

          const elem = document.getElementById(this.CEModuleRendererId);
          const links = [];
          function walk(node, callback) {
            callback(node);
            if (node && node.childNodes) {
              for (let i = 0; i < node.childNodes.length; i += 1) {
                walk(node.childNodes[i], callback);
              }
            }
          }
          walk(elem, (n) => {
            if (n && n.nodeName && n.nodeName.toLowerCase() === 'a') links.push(n);
          });
          links.forEach((cele) => {
            const href = cele.getAttribute('href');
            const label = cele.innerHTML;

            const match = interModuleLinkRegex.exec(href);
            if (match && match[0]) {
              const link = href.substring(match[0].length);
              /* eslint-disable no-param-reassign */
              cele.outerHTML = `<a href="${link}">${label}</a>`;
            } else if (urlRegex.test(href)) {
              /* eslint-disable no-param-reassign */
              cele.outerHTML = `<a href="${href}" target="_blank">${label}</a>`;
            } else if (intraModuleLinkRegex.test(href)) {
              // TODO - this markdown decoding might not be enough
              // Replace all %20 with spaces
              let pageName = href.substring(1).replace(/%20/g, ' ');

              // Newer versions of MS Word use dashes instead of %20 to
              // denote spaces. Replace them as well.
              pageName = pageName.substring(0).replace(/-/g, ' ');
              const pagePath = this.pageNameMap[this.cemoduleId][pageName];

              // Use the module Id from the router parameters
              const link = `/module/${this.cemoduleId}/page/${pagePath}`;
              /* eslint-disable no-param-reassign */
              cele.outerHTML = `<a href="${link}">${label}</a>`;
            }
          });
          initializeLazyImages();

          if (!this.pageCommitOID && !this.useHistoricalContext && !this.isPublicViewMode) {
            initializeLazyAssuranceDetails({
              moduleId: this.cemoduleId,
              path: this.pagePath
            });
          }
        }, 0);
      }
    }
  },

  methods: {
    snakeMe(title) {
      return title ? title.trim().toLowerCase().replace(/\s+/g, '-') : title;
    },
    isHtmlContent() {
      return !!this.htmlContent;
    },

    // NOTE: Uses regex, not a great way to parse HTML/md
    // Doesn't support html table links. Migrate to GHF markdown, tables?
    parseLinks(page) {
      // const regex = /(?:__|[*#])|\[(.*?)\]\((.*?)\)/gm;
      // const markdownLinkRegex = /(?!!*$)\[(.*?)\]\((.*?)\)/gm;
      const markdownLinkRegex = /\[([^[\]]+)\]\(([^)]+)\)/gm;

      const replacer = (str, label, href) => {
        // Check for inter-module link
        const interModuleLinkRegex = /^https?:\/\/ce-viewer.com/;
        const match = interModuleLinkRegex.exec(href);
        if (match && match[0]) {
          const link = href.substring(match[0].length);
          return `<a href="${link}">${label}</a>`;
        }

        // Check for external link
        // If external link then leave as is - but add target="_blank"
        // so it opens in a new tab/window.

        /* eslint-disable no-useless-escape */
        const urlRegex = new RegExp(
          [
            '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?',
            '[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?',
            '(\/.*)?$'
          ].join('')
        );
        /* eslint-disable no-useless-escape */
        if (urlRegex.test(href)) {
          return `<a href="${href}" target="_blank">${label}</a>`;
        }

        // Check for intra-module link - links within the same module
        // NOTE: This test is executed last as this regex can also be
        // triggered by the inter module link regex.
        const intraModuleLinkRegex = /^#/;

        if (intraModuleLinkRegex.test(href)) {
          // TODO - this markdown decoding might not be enough
          // Replace all %20 with spaces
          let pageName = href.substring(1).replace(/%20/g, ' ');

          // Newer versions of MS Word use dashes instead of %20 to
          // denote spaces. Replace them as well.
          pageName = pageName
            .substring(0)
            .replace(/-/g, ' ')
            .replace(/_/g, ' ')
            .toLowerCase()
            .trim();
          let pagePath = '';
          const modulePageNames = this.pageNameMap[this.cemoduleId];
          const moduleOriginalPageNames = this.pageOriginalNameMap[this.cemoduleId];

          if (modulePageNames) {
            Object.keys(modulePageNames).forEach((key) => {
              if (key === pageName) {
                pagePath = modulePageNames[pageName];
              }

              if (pagePath === '') {
                const newKey = key.replace(/’|'|,/g, '');
                if (newKey === pageName) {
                  pagePath = modulePageNames[key];
                }
                // maybe the page is locked by parent in child site
                // find its path from moduleOriginalPageNames
                if (pagePath === '' && moduleOriginalPageNames) {
                  Object.keys(moduleOriginalPageNames).forEach((originalKey) => {
                    if (originalKey === pageName) {
                      pagePath = moduleOriginalPageNames[pageName];
                    }

                    if (pagePath === '') {
                      const newOriginalKey = originalKey.replace(/’|'|,/g, '');
                      if (newOriginalKey === pageName) {
                        pagePath = moduleOriginalPageNames[originalKey];
                      }
                    }
                  });
                }
              }
            });
          }

          // Use the module Id from the router parameters
          const link = `https://ce-viewer.com/module/${this.cemoduleId}/page/${pagePath}`;
          return `<a href="${link}">${label}</a>`;
        }

        // If all else fails return the original string
        return str;
      };
      const pageWithoutMarkdownLink = page.replace(markdownLinkRegex, replacer);

      return pageWithoutMarkdownLink; // router-link behavior for internal navigation
    },
    onEditPopUp(key) {
      if (this.hasAccess('singlecontexteditTable')) {
        this.$refs['context-menu-dialog'].editPopup(key);
      } else {
        this.showErrorAlert('No access to edit context', false);
      }
    }
  }
};
</script>

<style scoped>
.CEModuleRenderer {
  margin: 0px 24px;
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
}

.CEModuleRenderer :deep(.pm-table-wrapper) {
  overflow: auto;
  margin-bottom: 16px;
}

.CEModuleRenderer :deep(table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

/* Legacy tables saved with an absolute pixel width on the table element itself must still be
   constrained to the container instead of overflowing it; per-cell widths are left alone so
   relative column proportions are preserved. */
.CEModuleRenderer :deep(table[style]) {
  max-width: 100% !important;
  min-width: 0 !important;
}

.CEModuleRenderer :deep(td),
div :deep(th) {
  border: 1px solid #dddddd;
  padding: 16px;
  vertical-align: top;
}

.CEModuleRenderer :deep(.pm-table-wrapper td),
.CEModuleRenderer :deep(.pm-table-wrapper th) {
  word-break: break-word;
}

/* Below tablet width, allow tables to exceed the viewport and become a horizontal scroll container. */
@media (max-width: 1024px) {
  .CEModuleRenderer :deep(.pm-table-wrapper table[style]) {
    max-width: none !important;
    min-width: var(--pm-table-width, 0) !important;
  }

  .CEModuleRenderer :deep(.pm-table-wrapper table:not([style])) {
    table-layout: auto;
    width: auto;
  }

  .CEModuleRenderer :deep(.pm-table-wrapper table:not([style]) td),
  .CEModuleRenderer :deep(.pm-table-wrapper table:not([style]) th) {
    min-width: 8rem;
    word-break: normal;
    overflow-wrap: normal;
  }
}

.CEModuleRenderer :deep(th) {
  text-align: left;
  background-color: #f2f2f2;
  color: black;
}

.CEModuleRenderer :deep(img) {
  display: block;
  margin: 0 auto 1rem auto;
  max-width: 80%;
  height: auto !important;
}

/*
 * Images WITHOUT an explicit width keep the legacy reset that overrides the
 * inline style created by the table-image import process.
 */
.CEModuleRenderer :deep(img:not([width])) {
  width: auto !important;
}

/*
 * Images WITH an explicit width (set via drag-resize) respect that width while
 * staying responsive within the content column.
 */
.CEModuleRenderer :deep(img[width]) {
  max-width: 100%;
}

/* Image layout modes (set via the image bubble menu / data-layout attribute).
   Wrap family: the image floats and text flows beside it. */
.CEModuleRenderer :deep(img[data-layout='inline']) {
  display: inline-block;
  margin: 0 0.25rem;
  vertical-align: middle;
}

.CEModuleRenderer :deep(img[data-layout='left-wrap']) {
  display: block;
  float: left;
  margin: 0.25rem 1rem 0.5rem 0;
}

.CEModuleRenderer :deep(img[data-layout='right-wrap']) {
  display: block;
  float: right;
  margin: 0.25rem 0 0.5rem 1rem;
}

/* No-wrap family: the image sits alone on its line. The three differ only in
   which side the auto margin falls on, so they stay visually consistent. */
.CEModuleRenderer :deep(img[data-layout='left']) {
  display: block;
  float: none;
  margin: 0.5rem auto 0.5rem 0;
}

.CEModuleRenderer :deep(img[data-layout='center']) {
  display: block;
  float: none;
  margin: 0.5rem auto;
}

.CEModuleRenderer :deep(img[data-layout='right']) {
  display: block;
  float: none;
  margin: 0.5rem 0 0.5rem auto;
}

.CEModuleRenderer :deep(pre) {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}

.CEModuleRenderer :deep(pre code) {
  display: block;
}

.v-application .CEModuleRenderer :deep(code) {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
}

.CEModuleRenderer :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  padding-left: 0.8rem;
  font-style: italic;
}

.CEModuleRenderer :deep(ol ol) {
  list-style-type: lower-alpha;
}

.CEModuleRenderer :deep(ol ol ol) {
  list-style-type: lower-roman;
}

/* Kludge for html2canvas used by html2pdf
Source: https://github.com/niklasvh/html2canvas/issues/796#issuecomment-185748809
*/
.container {
  font-family: Gilroy, sans-serif;
}

.title-split {
  border-bottom: 1px solid rgb(var(--v-theme-accent));
  margin-bottom: 24px;
}

@media (max-width: 768px) {
  .CEModuleRenderer {
    overflow: auto;
  }
}

.CEModuleRenderer :deep(.context-key-preferences) {
  border: 1px solid rgb(var(--v-theme-secondary));
  border-radius: 5px;
  margin-bottom: 1.5rem;
}
</style>
